import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { exportToCSV, exportToExcel } from "../../utils/exportUtils";
import styles from "./css/ExportBtn.module.css";

/**
 * Reusable Export Button component with responsive text, Framer Motion animations,
 * and CSV / Excel export dropdown options.
 */
const ExportBtn = ({
  onExport,
  columns = [],
  data = [],
  title = "transaction_ledger",
}) => {
  const shouldReduceMotion = useReducedMotion();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportDropdownRef = useRef(null);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExportCSV = () => {
    setShowExportMenu(false);
    if (typeof onExport === "function") {
      onExport("csv");
    } else {
      const fileName = (title || "transaction_ledger").toLowerCase().replace(/[^a-z0-9]/g, "_");
      exportToCSV(columns, data, `${fileName}.csv`);
    }
  };

  const handleExportExcel = () => {
    setShowExportMenu(false);
    if (typeof onExport === "function") {
      onExport("excel");
    } else {
      const fileName = (title || "transaction_ledger").toLowerCase().replace(/[^a-z0-9]/g, "_");
      exportToExcel(columns, data, `${fileName}.xls`);
    }
  };

  return (
    <div className={styles.exportDropdownWrapper} ref={exportDropdownRef}>
      <motion.button
        type="button"
        className={styles.btnExport}
        onClick={() => setShowExportMenu(!showExportMenu)}
        whileHover={shouldReduceMotion ? {} : { scale: 1.04, y: -2 }}
        whileTap={shouldReduceMotion ? {} : { scale: 0.96 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
      >
        <i className="fas fa-download"></i>
        <span className={styles.btnExportText}>Export</span>
        <i className={`fas fa-caret-down ms-1 ${styles.caretIcon} ${showExportMenu ? styles.caretRotated : ""}`}></i>
      </motion.button>

      <AnimatePresence>
        {showExportMenu && (
          <motion.div
            className={styles.dropdownMenu}
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.95 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              className={styles.dropdownItem}
              onClick={handleExportCSV}
            >
              <i className="fas fa-file-csv text-info"></i> Export as CSV (.csv)
            </button>

            <div className={styles.dropdownDivider}></div>

            <button
              type="button"
              className={styles.dropdownItem}
              onClick={handleExportExcel}
            >
              <i className="fas fa-file-excel text-success"></i> Export as Excel (.xls)
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExportBtn;
